import { useState } from 'react'
import { Link } from 'react-router-dom'
import { discordDocsPages as discordPages, formatDiscordDate } from '@/data/discordDocsUrls'
import { useDiscordDocsFreshness } from '@/hooks/useDiscordDocsFreshness'
import styles from '@/components/Discord/DiscordDocument.module.css'

export default function DiscordIndexPage() {
  const [query, setQuery] = useState('')
  const { data, loading, error } = useDiscordDocsFreshness()
  const normalize = (text: string) =>
    text
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLocaleLowerCase('cs')
  const pages = discordPages.filter((page) =>
    normalize(`${page.csTitle} ${page.description} ${page.category}`).includes(
      normalize(query.trim()),
    ),
  )
  const categories = [...new Set(pages.map((page) => page.category))]
  return (
    <div className={`${styles.index} ac-width`}>
      <header className={styles.indexHeader}>
        <h1>
          Discord.
          <br />
          Srozumitelně česky.
        </h1>
        <p>
          Pravidla, bezpečnost a podmínky služby na jednom místě. Komunitní překlady od Allcomu pro
          české a slovenské čtenáře.
        </p>
      </header>
      <div className={styles.indexTools}>
        <label htmlFor="docs-search">
          Najít dokument
          <input
            id="docs-search"
            type="search"
            placeholder="Například pravidla, věk nebo hlášení"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <p role="status">
          {pages.length} z {discordPages.length} dokumentů
        </p>
      </div>
      <p className={styles.indexNotice}>
        Nejde o oficiální překlady Discordu. Při nesrovnalostech platí originál, který je uvedený u
        každého dokumentu.
      </p>
      <p className={styles.freshness} role="status">
        {loading
          ? 'Ověřujeme obsah původních dokumentů…'
          : error
            ? 'Aktuálnost originálů se nepodařilo ověřit. U dokumentů zůstává uvedena poslední porovnaná verze.'
            : 'Data označují vydání originálu pokryté překladem. Každý dokument má historii změn a datum posledního porovnání.'}
      </p>
      {pages.length ? (
        categories.map((category) => (
          <section key={category} className={styles.indexSection} aria-label={category}>
            <h2>{category}</h2>
            <ul className={styles.documentList}>
              {pages
                .filter((page) => page.category === category)
                .map((page) => (
                  <li key={page.path}>
                    <Link to={page.path}>
                      <div>
                        <h3>{page.csTitle}</h3>
                        <p>{page.description}</p>
                      </div>
                      <span className={styles.documentMeta}>
                        Přeložený originál
                        <br />
                        {formatDiscordDate(page.ourDate)}
                        {(page.needsUpdate ||
                          data?.pages.find((item) => item.path === page.path)?.isOutdated ===
                            true) && <strong>Překlad vyžaduje aktualizaci</strong>}
                        {data?.pages.find((item) => item.path === page.path)?.error && (
                          <span>Originál se nepodařilo ověřit</span>
                        )}
                        {page.history.length > 0 && <span>Historie změn v dokumentu</span>}
                      </span>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        aria-hidden="true"
                      >
                        <path d="M5 12h14m-6-6 6 6-6 6" />
                      </svg>
                    </Link>
                  </li>
                ))}
            </ul>
          </section>
        ))
      ) : (
        <div className={styles.empty}>
          <h2>Takový dokument tu zatím není.</h2>
          <p>Zkus jiné slovo nebo si zobraz celou knihovnu.</p>
          <button type="button" onClick={() => setQuery('')}>
            Zobrazit všechny dokumenty
          </button>
        </div>
      )}
      <footer className={styles.indexFooter}>
        <p>Chybí překlad nebo jsi našel chybu?</p>
        <a href="https://discord.allcom.zone" target="_blank" rel="noopener noreferrer">
          Dej nám vědět na Discordu
        </a>
      </footer>
    </div>
  )
}
